import React, { useState, useEffect, useCallback } from 'react';
import { previewAPI } from '../utils/api';
import { Button } from './ui/button';
import { Play, Square, RefreshCw, ExternalLink, Loader2, Monitor } from 'lucide-react';
import { toast } from 'sonner';

export default function PreviewPanel({ projectId, stack }) {
  const [status, setStatus] = useState('stopped');
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const res = await previewAPI.status(projectId);
      setStatus(res.data.status);
      setUrl(res.data.url);
    } catch {
      setStatus('stopped');
    }
  }, [projectId]);

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 10000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  const handleStart = async () => {
    setLoading(true);
    try {
      const res = await previewAPI.start(projectId);
      setUrl(res.data.url);
      setStatus('running');
      toast.success('Preview started');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start preview');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    setLoading(true);
    try {
      await previewAPI.stop(projectId);
      setUrl(null);
      setStatus('stopped');
      toast.info('Preview stopped');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to stop preview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Monitor className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground">Preview</span>
          <span className={`w-2 h-2 rounded-full ${status === 'running' ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
        </div>
        <div className="flex items-center gap-1">
          {status === 'running' ? (
            <>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={handleStop} disabled={loading}>
                <Square className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => checkStatus()} disabled={loading}>
                <RefreshCw className="w-3 h-3" />
              </Button>
              {url && (
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => window.open(url, '_blank')}>
                  <ExternalLink className="w-3 h-3" />
                </Button>
              )}
            </>
          ) : (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleStart} disabled={loading}>
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              <span className="ml-1">Start</span>
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 relative">
        {status === 'running' && url ? (
          <iframe
            src={url}
            title="Project Preview"
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3">
            <Monitor className="w-10 h-10 opacity-20" />
            <p className="text-sm">
              {loading ? 'Starting preview...' : 'Click Start to launch a live preview'}
            </p>
            {!loading && (
              <p className="text-xs opacity-60">Requires Docker</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
